import React from 'react';
import {
  Card,
  Text,
  Badge,
  BlockStack,
  InlineStack,
  Button,
  Box,
  Link,
} from '@shopify/polaris';

import { redirectToTopLevel } from '../utils/topLevelRedirect';

/**
 * The in-app plan entry (apparelhub-shopify-app#12).
 *
 * Shopify rejected the app on requirement 1.2.1 because "paying for plans will
 * route us to an external billing method." Before this card there was no
 * billing UI here at all, only bare "Open ApparelHub.AI" links; a reviewer
 * followed one, landed on our pricing page, and was offered an external
 * checkout. This card is the compliant path a reviewer is meant to find, and it
 * keeps the merchant inside Shopify's own hosted billing flow.
 *
 * Two shapes, decided by who bills the account:
 *
 *   billing_provider === 'stripe'
 *     A pre-existing direct customer who happens to have connected a shop
 *     (epic apparelhub-ai#729 D6: "you do not need to change anything for your
 *     existing customers"). Offering them a Shopify plan here would start a
 *     SECOND subscription for a plan they already pay for, so their plan is
 *     shown read-only with a pointer to manage it where it actually lives.
 *
 *   anything else
 *     Shopify-billed or not yet paying. Plan selection goes to Shopify's hosted
 *     page, opened at the TOP level because the admin iframe cannot navigate
 *     itself there.
 *
 * Renders nothing until the account is connected and the platform has told us
 * something about billing. A card that says nothing is worse than no card.
 */
export default function BillingCard({ isConnected, billing, loading }) {
  if (loading || !isConnected || !billing) return null;

  const { provider, tierName, planSelectionUrl } = billing;

  // Nothing known about billing yet (older platform build, or a shop we could
  // not resolve to an account). Stay silent rather than guess.
  if (!provider && !tierName && !planSelectionUrl) return null;

  const stripeBilled = provider === 'stripe';

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Your plan</Text>
          {tierName ? <Badge tone="success">{tierName}</Badge> : null}
        </InlineStack>

        {stripeBilled ? (
          <>
            <Text as="p" variant="bodyMd" tone="subdued">
              Your plan is billed directly through ApparelHub, so it is managed
              there rather than in Shopify.
            </Text>
            <Box>
              <Link url="https://apparelhub.ai/billing/subscription" external>
                Manage your plan
              </Link>
            </Box>
          </>
        ) : (
          <>
            <Text as="p" variant="bodyMd" tone="subdued">
              {tierName
                ? 'Change your plan or see what is included in each one. Billing is handled by Shopify and appears on your Shopify invoice.'
                : 'Choose a plan to unlock design generation, product sync, and automated fulfillment. Billing is handled by Shopify and appears on your Shopify invoice.'}
            </Text>
            {planSelectionUrl ? (
              <Box>
                <Button
                  variant="primary"
                  url={planSelectionUrl}
                  target="_top"
                  onClick={(event) => {
                    // Belt and braces. The anchor's target="_top" is what makes
                    // keyboard and middle-click work; this handles the case
                    // where the embedded frame swallows the default navigation.
                    if (event?.preventDefault) event.preventDefault();
                    redirectToTopLevel(planSelectionUrl);
                  }}
                >
                  {tierName ? 'View plans' : 'Choose a plan'}
                </Button>
              </Box>
            ) : (
              <Text as="p" variant="bodyMd" tone="subdued">
                Plans are available from the Apps section of your Shopify admin.
              </Text>
            )}
          </>
        )}
      </BlockStack>
    </Card>
  );
}
